import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "src/store";
import { selectBriefs } from "../brief/selectors";
import { BriefType } from "../brief/types";
import { SystemState } from "./types";

const selectSystem = (state: RootState) => state.system;

export const selectCurrentTab = createSelector(
  selectSystem,
  (state: SystemState) => state.currentTab
);

export const selectPlans = createSelector(
  selectSystem,
  (state: SystemState) => state.plans
);

export const selectBriefsPage = createSelector(
  selectSystem,
  (state: SystemState) => state.briefs.page
);

export const selectPublicPlans = createSelector(
  selectSystem,
  (state: SystemState) =>
    state.plans
      .filter((plan) => !plan.hidden)
      .map((plan) => {
        if (plan.paddlePlanId === state.user.paddlePlan?.subscription_plan_id) {
          return { ...plan, isActive: true };
        }
        return plan;
      })
);

export const selectCurrentPlan = createSelector(selectSystem, (system) => {
  const activePlan = system.plans.find(
    (plan) => plan.paddlePlanId === system.user.paddlePlan?.subscription_plan_id
  );
  const metadata = activePlan?.features?.reduce(function (pV, cV, index) {
    pV[cV.name] = cV.value;
    return pV;
  }, {});
  return system.user.paddlePlan?.next_bill_date
    ? { ...metadata, ...system.user.paddlePlan }
    : system.user.stripePlan;
});

export const selectInvoices = createSelector(
  selectSystem,
  (state: SystemState) => state.invoices
);

export const selectIsUpgradeModalOpen = createSelector(
  selectSystem,
  (state: SystemState) => state.isUpgradeModalOpen
);

export const selectIsBriefLimit = createSelector(
  selectSystem,
  (state: SystemState) => state.isBriefLimitReached
);

export const selectIsAIWordsUpgradeModalOpen = createSelector(
  selectSystem,
  (state: SystemState) => state.isAIWordsUpgradeModalOpen
);

export const AIwordsModalText = createSelector(
  selectSystem,
  (state: SystemState) => state.aiUpgradeModalText
);

export const selectUserRole = createSelector(
  selectSystem,
  (state: SystemState) => state.user.role
);


export const selectIsBriefLimitReached = createSelector(
  [selectSystem, selectBriefs],
  (system: SystemState, briefs: BriefType[]) => {
    const currentPlan = system.plans.find(({ isActive }) => isActive);

    return currentPlan.limitReached;
  }
);

export const selectLastSaved = createSelector(
  selectSystem,
  (system) => system.lastSaved
);

export const selectUser = createSelector(
  selectSystem,
  (system: SystemState) => system.user
);

export const selectExportInHtml = createSelector(
  selectUser,
  (user) => user?.exportInHtml ?? false
);

export const selectUserIsLoading = createSelector(
  selectUser,
  (user) => user.isLoading
);

export const selectUsers = createSelector(
  selectSystem,
  (system) => system.users
);

export const selectUserBriefs = createSelector(
  selectUser,
  (user) => user.briefs
);
export const selectBriefsStatistics = createSelector(
  selectSystem,
  (system) => system.statistics.briefs
);

export const selectPaddleUsers = createSelector(
  selectSystem,
  (system) => system.paddleUsers
);
export const isContentGapReady = createSelector(
  selectSystem,
  (system) => system.isContentGapReady
);